/**
 * RolesPageV2 — redesigned Role Management list.
 *
 * Layout: PageHeader + LibTable. Name (link to edit) +
 * description sub-line, admin count (mono), created date,
 * hover-revealed Edit + Delete.
 */
import React, { useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import Swal from 'sweetalert2';
import {
  deleteRole,
  fetchAllRoles,
} from '../../../../actions/rolesActions';
import LoadingBackdrop from '../../../Common/LoadingBackdrop';
import {
  PageHeader,
  Button,
  LibTable,
  IconButton,
  cn,
} from '../../../redesign';

const PlusSign = (
  <span style={{ fontSize: 16, lineHeight: 1 }} aria-hidden>+</span>
);

const EditIconSvg = (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round">
    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
    <path d="M18.5 2.5a2.12 2.12 0 0 1 3 3L12 15l-4 1 1-4z" />
  </svg>
);

const TrashIconSvg = (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="3 6 5 6 21 6" />
    <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
    <path d="M10 11v6M14 11v6" />
    <path d="M9 6V4a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v2" />
  </svg>
);

function formatDate(iso) {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  } catch {
    return '';
  }
}

export default function RolesPageV2() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const loading = useSelector((s) => s.roles.loading);
  const roles = useSelector((s) => s.roles.roles);

  useEffect(() => {
    dispatch(fetchAllRoles());
  }, [dispatch]);

  const handleDelete = (id, name) => {
    Swal.fire({
      title: 'Are you sure?',
      text: `Delete role "${name}"? You won't be able to recover it.`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Yes, delete it!',
      cancelButtonText: 'No, cancel',
    }).then((result) => {
      if (result.value) dispatch(deleteRole(id));
    });
  };

  const total = Array.isArray(roles) ? roles.length : undefined;
  const totalLabel = total != null ? `${total.toLocaleString()} total` : undefined;

  return (
    <div className="bg-bg min-h-full">
      <LoadingBackdrop open={!!loading} />
      <PageHeader
        eyebrow="Settings & Admin"
        title="Role Management"
        total={totalLabel}
        actions={
          <Link to="/roles/create" className="no-underline">
            <Button variant="coral" leadingIcon={PlusSign}>
              Add new role
            </Button>
          </Link>
        }
      />

      <div style={{ padding: '0 28px 32px' }}>
        {!Array.isArray(roles) || roles.length === 0 ? (
          <div
            className="bg-surface border border-sand text-text-muted italic text-center"
            style={{ borderRadius: 12, padding: '40px 16px', fontSize: 13 }}
          >
            {loading ? 'Loading roles…' : 'No roles yet — add one to get started.'}
          </div>
        ) : (
          <LibTable
            fixed
            className="border border-sand"
            style={{ borderRadius: 12, overflow: 'hidden' }}
          >
            <colgroup>
              {/* name + desc */}
              <col />
              {/* admin count */}
              <col style={{ width: 110 }} />
              {/* created */}
              <col style={{ width: 130 }} />
              {/* actions */}
              <col style={{ width: 100 }} />
            </colgroup>
            <LibTable.Head>
              <tr>
                <LibTable.Th>Role</LibTable.Th>
                <LibTable.NumTh>Admins</LibTable.NumTh>
                <LibTable.Th>Created</LibTable.Th>
                <LibTable.Th style={{ textAlign: 'right' }}>Actions</LibTable.Th>
              </tr>
            </LibTable.Head>
            <tbody>
              {roles.map((r) => (
                <LibTable.Row key={r.role_id}>
                  <LibTable.Td>
                    <Link
                      to={`/roles/${r.role_id}/edit`}
                      className="font-semibold text-navy no-underline hover:text-teal-green-dark block truncate"
                      style={{ fontSize: 13.5, lineHeight: 1.25 }}
                      title={r.role_name || ''}
                    >
                      {r.role_name || 'Untitled'}
                    </Link>
                    {r.role_description && (
                      <div
                        className="text-text-muted truncate"
                        style={{ fontSize: 11.5, marginTop: 2 }}
                        title={r.role_description}
                      >
                        {r.role_description}
                      </div>
                    )}
                  </LibTable.Td>
                  <LibTable.NumTd muted={!Number(r.admin_count)}>
                    {Number(r.admin_count) || 0}
                  </LibTable.NumTd>
                  <LibTable.Td>
                    <span
                      className="font-mono text-text-muted"
                      style={{ fontSize: 11.5 }}
                    >
                      {formatDate(r.created_at)}
                    </span>
                  </LibTable.Td>
                  <LibTable.Td style={{ textAlign: 'right' }}>
                    <div
                      className={cn(
                        'inline-flex opacity-0 transition-opacity duration-150',
                        'group-hover:opacity-100',
                      )}
                      style={{ gap: 4 }}
                    >
                      <Link
                        to={`/roles/${r.role_id}/edit`}
                        aria-label={`Edit ${r.role_name}`}
                      >
                        <IconButton size="sm" aria-label="Edit">{EditIconSvg}</IconButton>
                      </Link>
                      <IconButton
                        size="sm"
                        aria-label="Delete"
                        title="Delete"
                        onClick={() => handleDelete(r.role_id, r.role_name)}
                      >
                        {TrashIconSvg}
                      </IconButton>
                    </div>
                  </LibTable.Td>
                </LibTable.Row>
              ))}
            </tbody>
          </LibTable>
        )}
      </div>
    </div>
  );
}
